import { useCallback, useEffect, useState } from 'react';
import useLocalStorage from '@alltrails/local-storage/useLocalStorage';

/**
 * useInstallPrompt determines whether or not to display the prompt.
 * Banners & interstitials can only open if a certain duration of time has passed since they were dismissed.
 * The dismissal time is stored in local storage, and read when attempting to open the component again.
 */

const oneDay = 60 * 60 * 24 * 1000;
const oneWeek = 60 * 60 * 24 * 7 * 1000;

export enum DeepLinkLocalStorageKeys {
  BANNER = 'banner-dismissal-date',
  INTERSTITIAL = 'interstitial-dismissal-date',
  /**
   * Shared dismissal key for contextual install prompts (e.g. trail map open, directions CTA)
   */
  CONTEXTUAL_INSTALL_PROMPT = 'contextual-install-prompt-dismissal-date'
}

const bannerData = {
  localStorageKey: DeepLinkLocalStorageKeys.BANNER,
  dismissalDuration: oneDay
};

const interstitialData = {
  localStorageKey: DeepLinkLocalStorageKeys.INTERSTITIAL,
  dismissalDuration: oneWeek
};

const contextualInstallPromptData = {
  localStorageKey: DeepLinkLocalStorageKeys.CONTEXTUAL_INSTALL_PROMPT,
  dismissalDuration: oneDay
};

const didPassDismissalDuration = (existingDismissalDate: number | null, duration: number) => {
  // Display the prompt if no dismissal date exists yet.
  if (!existingDismissalDate) {
    return true;
  }

  if (existingDismissalDate) {
    const now = new Date().getTime();
    if (now - existingDismissalDate >= duration) {
      return true;
    }
  }

  return false;
};

type DeeplinkHookReturnType = [boolean, (shouldOpenDeeplinkComponent: boolean) => boolean];

const useInstallPrompt = (
  promptType: 'banner' | 'interstitial' | 'contextual-install-prompt',
  openOnMount: boolean,
  overrideSnooze?: boolean
): DeeplinkHookReturnType => {
  const [isOpenRequested, setIsOpenRequested] = useState<boolean | null>(null);
  let promptData;
  if (promptType === 'interstitial') {
    promptData = interstitialData;
  } else if (promptType === 'contextual-install-prompt') {
    promptData = contextualInstallPromptData;
  } else {
    promptData = bannerData;
  }
  const { dismissalDuration, localStorageKey } = promptData;
  const { getValue, setValue } = useLocalStorage<number>(localStorageKey);

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const isOpen = isMounted && (isOpenRequested ?? openOnMount) && (overrideSnooze || didPassDismissalDuration(getValue(), dismissalDuration));

  const setIsOpenAttempt = useCallback(
    (shouldOpen: boolean): boolean => {
      if (shouldOpen) {
        setIsOpenRequested(true);
        return overrideSnooze || didPassDismissalDuration(getValue(), dismissalDuration);
      }

      if (!overrideSnooze) {
        setValue(new Date().getTime());
      }
      setIsOpenRequested(false);
      return false;
    },
    [getValue, overrideSnooze, dismissalDuration, setValue]
  );

  return [isOpen, setIsOpenAttempt];
};

export default useInstallPrompt;
